import type { CSSProperties, ComponentPropsWithRef } from "react";
import { forwardRef } from "react";
import { Img, Link, Section } from "@react-email/components";
import { useAttachmentMetadata } from "@circle-react-shared/BuilderUI/Media/AttachmentContext";
import type { StyleValue } from "@circle-react-shared/BuilderUI/StyleValue.types";
import { useGoober } from "@circle-react-shared/Emails/VisualEmailBuilder/Design/GooberContext";
import { mobileMediaQuery } from "@circle-react-shared/Emails/VisualEmailBuilder/Design/mediaQuery";
import { useParseStyles } from "@circle-react-shared/Emails/VisualEmailBuilder/Design/useParseStyles";
import { stripPx } from "@circle-react-shared/Emails/VisualEmailBuilder/Utils/stripPx";
import { defaultStyles } from "./defaultStyles";
import defaultPlaceholderImage from "./img_preview_large.png";

interface ImageProps extends Omit<ComponentPropsWithRef<"table">, "loading"> {
  alt?: string;
  "data-preview-mode": boolean;
  href?: string;
  image: {
    signed_id: string;
    placeholder?: string;
  };
  loading?: "lazy" | "eager";
  styles?: Record<string, StyleValue>;
}

export const Image = forwardRef<HTMLTableElement, ImageProps>(
  (
    {
      image,
      styles = {},
      alt = "",
      href,
      loading = "lazy",
      "data-preview-mode": isPreviewMode,
      ...rest
    },
    ref,
  ) => {
    const { css } = useGoober();

    const inlineStyles = useParseStyles({
      baseStyles: {
        display: "block",
      },
      styles,
      defaultStyles,
    });

    // Removing margin from the <img> and applying the same spacing as padding
    // on the container. Some clients ignore margins on the inner elements.
    const { margin, ...imgStyle } = inlineStyles;

    const sectionStyle: CSSProperties = {
      padding: margin,
    };

    const attachmentMetadata = useAttachmentMetadata(image.signed_id);
    const { url } = attachmentMetadata ?? {};
    const imageSrc = url ?? image.placeholder ?? defaultPlaceholderImage;

    const ImageElement = (
      <Img
        src={imageSrc}
        width={stripPx(inlineStyles.width)}
        height={stripPx(inlineStyles.height)}
        alt={alt}
        style={imgStyle}
        loading={loading}
        className={css`
          ${mobileMediaQuery} {
            width: 100% !important;
            margin: 0 !important;
          }
        `}
      />
    );

    return (
      <Section ref={ref} style={sectionStyle} {...rest}>
        {href && !isPreviewMode ? (
          <Link href={href}>{ImageElement}</Link>
        ) : (
          ImageElement
        )}
      </Section>
    );
  },
);

Image.displayName = "Image";
